import {
    ColorfinderImageBatch,
    Hotspot,
    HotspotBatch,
    InfraredImageBatch,
    NovaEntity,
    OrthoImageBatch,
} from "@nova/protobuf"
import { Builder } from "."
import { buildEntityCases } from "../buildEntityCases"
import { SocketSlice, SocketSliceEventHandler } from "./types"
import {
    batchUpdateEntitiesSocket,
    rejectCreate,
    rejectUpdate,
    ClientSocketEvent,
    handleIncomingSocketEvents,
} from "features/entity-caching/project/slice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import type { UpdateHotspotArgs } from "@nova/serializer/src/client/validators/cubes/hotspot"
import { ProtobufEvents } from "@nova/serializer/src/client/events"
import { collect } from "nodash"
import store from "integrations/redux/store"

const processEvent = collect((events: ClientSocketEvent[]) => {
    store.dispatch(handleIncomingSocketEvents({ events }))
}, 1000)

type RejectNotificationConfig =
    | { suppressErrorNotification: true }
    | {
          suppressErrorNotification?: false
          customMessage?: string
          status?: GlobalNotificationStatus
          timeoutInMs?: number
      }

const leafCollection = (path: (string | object)[]) =>
    [...path].reverse().find((seg): seg is string => typeof seg === "string")

const createRejectConfig: Record<string, RejectNotificationConfig> = {
    images: { customMessage: "Failed to add image to the layer." },
    hotspots: { customMessage: "Failed to create hotspot layer. Please try again." },

    lines: { customMessage: "Failed to create line layer. Please try again." },
    perimeters: { customMessage: "Failed to create perimeter layer. Please try again." },
    rulers: { customMessage: "Failed to create ruler layer. Please try again." },
    texts: { customMessage: "Failed to create text layer. Please try again." },
    icons: { customMessage: "Failed to create icon layer. Please try again." },
    orthoMasks: { customMessage: "Failed to create mask layer. Please try again." },
    groups: { customMessage: "Failed to create group layer. Please try again." },
    PDFs: { customMessage: "Failed to create PDF layer. Please try again." },
    PDFRasters: { customMessage: "Failed to create PDF layer. Please try again." },

    videoFlights: { customMessage: "Failed to create video layer. Please try again." },
    orthoFlights: { customMessage: "Failed to create orthomosaic layer. Please try again." },
    infraredFlights: { customMessage: "Failed to create infrared layer. Please try again." },
    panoramaFlights: { customMessage: "Failed to create panorama layer. Please try again." },
    liveVideoFlights: { customMessage: "Failed to create live video layer. Please try again." },
    mergedFlights: { customMessage: "Failed to create merged layer. Please try again." },
    imageColorFinderFlights: {
        customMessage: "Failed to create color finder layer. Please try again.",
    },
    videoColorFinderFlights: {
        customMessage: "Failed to create color finder layer. Please try again.",
    },

    default: { customMessage: "Failed to create layer. Please try again." },
}

export default {
    handlers: (builder: Builder) => buildEntityCases(builder),
    events: {
        add: () => (data, path) => {
            processEvent({ type: "add", data, path })
        },
        update: () => (updates, path) => {
            processEvent({ type: "update", data: updates, path })
        },
        delete: () => (path) => {
            processEvent({ type: "delete", path })
        },
        batchAdd: () => (proto: Uint8Array, path) => {
            if (path.length < 3) return
            if (path[0] === "infraredFlights" && path[2] === "hotspots") {
                const hotspotBatch = HotspotBatch.decode(new Uint8Array(proto))
                processEvent({
                    type: "batchAdd",
                    data: { entities: hotspotBatch.entities },
                    path,
                })
                return
            }
            if (path[0] === "infraredFlights" && path[2] === "images") {
                const hotspotImageBatch = InfraredImageBatch.decode(new Uint8Array(proto))
                processEvent({
                    type: "batchAdd",
                    data: { entities: hotspotImageBatch.entities },
                    path,
                })
                return
            }
            if (path[0] === "orthoFlights" && path[2] === "images") {
                const orthoImageBatch = OrthoImageBatch.decode(new Uint8Array(proto))
                processEvent({
                    type: "batchAdd",
                    data: { entities: orthoImageBatch.entities },
                    path,
                })
                return
            }
            if (path[0] === "imageColorFinderFlights" && path[2] === "images") {
                const colorfinderImageBatch = ColorfinderImageBatch.decode(new Uint8Array(proto))
                processEvent({
                    type: "batchAdd",
                    data: { entities: colorfinderImageBatch.entities },
                    path,
                })

                return
            }
        },
        batchUpdate: () => (data, path, groupKey) => {
            processEvent({ type: "batchUpdate", data, path, groupKey })
        },
        batchEntitiesUpdate:
            () =>
            (
                updates: Partial<Pick<Hotspot, UpdateHotspotArgs[number]>>,
                path: (string | object)[],
                ids: string[]
            ) => {
                batchUpdateEntitiesSocket({ type: "batchEntitiesUpdate", updates, path, ids })
            },
        replace: () => (data, path) => {
            processEvent({ type: "replace", data, path })
        },
        // prototodo: reject delete
        rejectCreate: (store) => (path: (string | object)[], errors: string[]) => {
            console.error("Create rejected with errors", errors)
            store.dispatch(rejectCreate({ path }))

            const config =
                createRejectConfig[leafCollection(path) ?? "default"] ?? createRejectConfig.default
            if (config.suppressErrorNotification) return

            store.dispatch(
                setGlobalNotificationContext({
                    message: config.customMessage ?? "Server rejected create",
                    status: config.status ?? GlobalNotificationStatus.error,
                    timeoutInMs: config.timeoutInMs ?? 3000,
                })
            )
        },
        rejectUpdate: (store) => (path: string[], data: NovaEntity, errors: string) => {
            console.error("Update rejected with errors", errors)
            store.dispatch(rejectUpdate({ path, data }))
            store.dispatch(
                setGlobalNotificationContext({
                    message: "Server rejected update",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 3000,
                })
            )
        },
    } satisfies { [K in keyof ProtobufEvents]: SocketSliceEventHandler } & {
        [k: string]: SocketSliceEventHandler
    },
} as SocketSlice
